import { isAuthenticated, isRefreshable, useAuthStore } from "@/auth/store";
import { api } from "@/api/client";

// Background refresh: rotate the access token ~5 min before its TTL.
// Started once from main.tsx; tears itself down on logout.

interface RefreshResponse {
  access_token: string;
  access_token_expires_at: string;
  refresh_token: string;
  refresh_token_expires_at: string;
  user: { id: string; email: string; created_at: string; role?: "user" | "admin" };
}

// Synchronous-from-the-caller's-POV session check used by route
// `beforeLoad` guards. Returns true if the user has a usable access
// token now, or if a refresh succeeds. Returns false otherwise (caller
// redirects to /login). Crucial on page reload: `accessToken` is
// memory-only (intentionally not persisted) but `refreshToken` survives,
// so a fresh app boot needs to opportunistically swap one for the other
// before deciding to kick the user out.
export async function ensureSession(): Promise<boolean> {
  if (isAuthenticated()) return true;
  if (!isRefreshable()) return false;
  const rt = useAuthStore.getState().refreshToken!;
  try {
    const body = await api.post<RefreshResponse>(
      "/auth/refresh",
      { refresh_token: rt },
      { skipAuth: true },
    );
    useAuthStore.getState().setSession(body);
    return true;
  } catch {
    // Refresh token rejected (revoked / expired / server-side
    // invalidation). Clear so the next render goes straight to /login
    // instead of looping through this on every navigation.
    useAuthStore.getState().clear();
    return false;
  }
}

const REFRESH_LEAD_MS = 5 * 60_000;
let timer: ReturnType<typeof setTimeout> | null = null;

function clear() { if (timer) { clearTimeout(timer); timer = null; } }

function schedule(): void {
  clear();
  const { accessTokenExp, refreshToken } = useAuthStore.getState();
  if (!accessTokenExp || !refreshToken) return;

  const fireAt = accessTokenExp - REFRESH_LEAD_MS;
  const delay = Math.max(0, fireAt - Date.now());

  timer = setTimeout(async () => {
    const rt = useAuthStore.getState().refreshToken;
    if (!rt) return;
    try {
      const body = await api.post<{
        access_token: string; access_token_expires_at: string;
        refresh_token: string; refresh_token_expires_at: string;
        user: { id: string; email: string; created_at: string; role?: "user" | "admin" };
      }>("/auth/refresh", { refresh_token: rt }, { skipAuth: true });
      useAuthStore.getState().setSession(body);
      // re-schedule against the new exp
      schedule();
    } catch {
      // Silent — next API call will hit 401 and trigger interactive
      // refresh-or-logout in the fetch wrapper.
    }
  }, delay);
}

export function startAuthRefreshLoop(): () => void {
  schedule();
  // Re-schedule whenever the access token changes (login, manual
  // refresh from a 401, or logout). zustand subscribe gives us the
  // exp value; compare to old to avoid restart on unrelated state.
  let lastExp = useAuthStore.getState().accessTokenExp;
  const unsub = useAuthStore.subscribe((state) => {
    if (state.accessTokenExp !== lastExp) {
      lastExp = state.accessTokenExp;
      schedule();
    }
  });
  return () => { unsub(); clear(); };
}
